import { useState, useEffect } from 'react';
import api from '../api';

// API Endpoint definitions with test cases
const API_ENDPOINTS = {
  auth: {
    name: 'Authentication & User',
    icon: '🔐',
    endpoints: [
      { method: 'GET', path: '/users/profile', name: 'Get User Profile', description: 'Returns authenticated user profile' },
    ]
  },
  bootstrap: {
    name: 'Bootstrap & Config (v2)',
    icon: '🚀',
    endpoints: [
      { method: 'GET', path: '/../v2/config', name: 'Unified Config', description: 'All app config - xp, tiers, status, casino, achievements', isV2: true },
    ]
  },
  status: {
    name: 'Status System',
    icon: '⭐',
    endpoints: [
      { method: 'GET', path: '/status-system/my-status', name: 'My Status', description: 'Current user tier, level, XP, progress' },
      { method: 'GET', path: '/status-system/config', name: 'Status Config', description: 'Full status system configuration' },
      { method: 'GET', path: '/status-system/tiers', name: 'Tier Config', description: 'All tier definitions and XP thresholds' },
    ]
  },
  games: {
    name: 'Games & Tables',
    icon: '🎰',
    endpoints: [
      { method: 'GET', path: '/games/live', name: 'Live Games', description: 'All currently running games with player counts' },
    ]
  },
  tablecaptain: {
    name: 'TableCaptain Bridge',
    icon: '🌉',
    endpoints: [
      { method: 'GET', path: '/tablecaptain/health', name: 'Health Check', description: 'Check TableCaptain Bridge connectivity' },
      { method: 'GET', path: '/tablecaptain/games', name: 'TC Games', description: 'Games from TableCaptain with player counts' },
    ]
  },
  achievements: {
    name: 'Achievements',
    icon: '🏆',
    endpoints: [
      { method: 'GET', path: '/settings/achievements', name: 'All Achievements', description: 'List all achievement definitions' },
    ]
  },
  rewards: {
    name: 'Rewards',
    icon: '🎁',
    endpoints: [
      { method: 'GET', path: '/rewards', name: 'Available Rewards', description: 'Rewards available to current user' },
    ]
  },
  pokeratlas: {
    name: 'PokerAtlas',
    icon: '🃏',
    endpoints: [
      { method: 'GET', path: '/pokeratlas/status', name: 'PA Status', description: 'Check if PokerAtlas account is linked' },
    ]
  },
  notifications: {
    name: 'Notifications',
    icon: '🔔',
    endpoints: [
      { method: 'GET', path: '/notifications/preferences', name: 'Preferences', description: 'User notification preferences' },
    ]
  },
  settings: {
    name: 'Settings & Info',
    icon: '⚙️',
    endpoints: [
      { method: 'GET', path: '/casino-info', name: 'Casino Info', description: 'Casino name, address, hours, contact' },
      { method: 'GET', path: '/settings/tiers', name: 'Tier Thresholds', description: 'Legacy tier threshold settings' },
    ]
  },
  admin: {
    name: 'Admin Endpoints',
    icon: '👑',
    endpoints: [
      { method: 'GET', path: '/admin/dashboard', name: 'Dashboard Stats', description: 'Admin dashboard statistics' },
      { method: 'GET', path: '/admin/users?limit=5', name: 'Users (sample)', description: 'List users (limited to 5)' },
      { method: 'GET', path: '/admin/activity?limit=10', name: 'Activity Log', description: 'Recent admin activity' },
      { method: 'GET', path: '/admin/rewards', name: 'All Rewards', description: 'All custom rewards' },
      { method: 'GET', path: '/admin/staff-pins', name: 'Staff PINs', description: 'List staff PIN entries' },
      { method: 'GET', path: '/admin/rewards/log/redemptions?limit=10', name: 'Recent Redemptions', description: 'Recent reward redemptions' },
    ]
  },
  statusAdmin: {
    name: 'Status Admin',
    icon: '📊',
    endpoints: [
      { method: 'GET', path: '/status-system/config', name: 'Status Config (Public)', description: 'Full status system config' },
    ]
  },
  devTools: {
    name: 'Dev Tools',
    icon: '🔧',
    endpoints: [
      { method: 'GET', path: '/admin/dev/health/database', name: 'Database Health', description: 'Check database connectivity' },
      { method: 'GET', path: '/admin/dev/health/firebase', name: 'Firebase Health', description: 'Check Firebase connectivity' },
      { method: 'GET', path: '/admin/dev/cache/status', name: 'Cache Status', description: 'View current cache configuration' },
      { method: 'POST', path: '/admin/dev/cache/refresh', name: 'Refresh Caches', description: 'Clear all caches (config, game types)' },
    ]
  },
};

// Tier definitions for the simulator
const TIER_LEVELS = [
  { tier: 'vip', level: 1, name: 'VIP I', xp: 0 },
  { tier: 'vip', level: 2, name: 'VIP II', xp: 500 },
  { tier: 'vip', level: 3, name: 'VIP III', xp: 1500 },
  { tier: 'elite', level: 4, name: 'Elite I', xp: 3000 },
  { tier: 'elite', level: 5, name: 'Elite II', xp: 6000 },
  { tier: 'elite', level: 6, name: 'Elite III', xp: 10000 },
  { tier: 'ambassador', level: 7, name: 'Ambassador I', xp: 15000 },
  { tier: 'ambassador', level: 8, name: 'Ambassador II', xp: 22000 },
  { tier: 'ambassador', level: 9, name: 'Ambassador III', xp: 30000 },
  { tier: 'boardmember', level: 10, name: 'Boardmember I', xp: 40000 },
  { tier: 'boardmember', level: 11, name: 'Boardmember II', xp: 55000 },
  { tier: 'boardmember', level: 12, name: 'Boardmember III', xp: 75000 },
  { tier: 'chairman', level: 13, name: 'Chairman', xp: 100000 },
];

function TestStatus({ status }) {
  const statusConfig = {
    pending: { bg: 'bg-gray-700', text: 'text-gray-400', label: 'Pending' },
    running: { bg: 'bg-blue-900', text: 'text-blue-400', label: 'Running...' },
    success: { bg: 'bg-green-900', text: 'text-green-400', label: 'Success' },
    error: { bg: 'bg-red-900', text: 'text-red-400', label: 'Failed' },
  };
  const config = statusConfig[status] || statusConfig.pending;
  return (
    <span className={`px-2 py-1 rounded text-xs font-medium ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  );
}

function formatTime(ms) {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}

function formatJson(data) {
  try {
    return JSON.stringify(data, null, 2);
  } catch {
    return String(data);
  }
}

// Tab components
function ApiTestingTab({ results, setResults, expandedResults, setExpandedResults, isRunningAll, setIsRunningAll, selectedCategory, setSelectedCategory, apiBaseUrl }) {
  
  const runTest = async (category, endpoint) => {
    const key = `${category}-${endpoint.path}`;
    setResults(prev => ({ ...prev, [key]: { status: 'running', response: null, time: null, error: null } }));
    const startTime = Date.now();
    
    try {
      const response = await api({ method: endpoint.method, url: endpoint.path });
      const endTime = Date.now();
      setResults(prev => ({
        ...prev,
        [key]: { status: 'success', response: response.data, time: endTime - startTime, statusCode: response.status, error: null }
      }));
    } catch (error) {
      const endTime = Date.now();
      setResults(prev => ({
        ...prev,
        [key]: { status: 'error', response: error.response?.data || null, time: endTime - startTime, statusCode: error.response?.status || 0, error: error.message }
      }));
    }
  };

  const runCategoryTests = async (categoryKey) => {
    const category = API_ENDPOINTS[categoryKey];
    for (const endpoint of category.endpoints) {
      await runTest(categoryKey, endpoint);
    }
  };

  const runAllTests = async () => {
    setIsRunningAll(true);
    setResults({});
    for (const [categoryKey, category] of Object.entries(API_ENDPOINTS)) {
      if (selectedCategory === 'all' || selectedCategory === categoryKey) {
        for (const endpoint of category.endpoints) {
          await runTest(categoryKey, endpoint);
        }
      }
    }
    setIsRunningAll(false);
  };

  const toggleExpanded = (key) => {
    setExpandedResults(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const testSummary = Object.values(results);
  const total = testSummary.length;
  const passed = testSummary.filter(r => r.status === 'success').length;
  const failed = testSummary.filter(r => r.status === 'error').length;

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-400">Category:</label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="bg-gray-700 border border-gray-600 rounded px-3 py-1.5 text-white text-sm focus:ring-2 focus:ring-amber-500"
            >
              <option value="all">All Categories</option>
              {Object.entries(API_ENDPOINTS).map(([key, cat]) => (
                <option key={key} value={key}>{cat.icon} {cat.name}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2 ml-auto">
            {total > 0 && (
              <div className="flex items-center gap-3 text-sm mr-4">
                <span className="text-green-400">{passed} passed</span>
                <span className="text-red-400">{failed} failed</span>
                <span className="text-gray-500">/ {total}</span>
              </div>
            )}
            <button
              onClick={runAllTests}
              disabled={isRunningAll}
              className="bg-amber-600 hover:bg-amber-700 disabled:bg-gray-600 text-white px-4 py-2 rounded font-medium text-sm flex items-center gap-2"
            >
              {isRunningAll ? (
                <><svg className="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg> Running...</>
              ) : '▶ Run All Tests'}
            </button>
            <button onClick={() => setResults({})} className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded font-medium text-sm">Clear</button>
          </div>
        </div>
      </div>

      {/* Endpoint Categories */}
      <div className="space-y-4">
        {Object.entries(API_ENDPOINTS).map(([categoryKey, category]) => {
          if (selectedCategory !== 'all' && selectedCategory !== categoryKey) return null;
          return (
            <div key={categoryKey} className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
              <div className="bg-gray-750 px-4 py-3 border-b border-gray-700 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-xl">{category.icon}</span>
                  <h2 className="text-lg font-semibold text-white">{category.name}</h2>
                  <span className="text-xs text-gray-500 bg-gray-700 px-2 py-0.5 rounded">{category.endpoints.length}</span>
                </div>
                <button onClick={() => runCategoryTests(categoryKey)} disabled={isRunningAll} className="text-amber-400 hover:text-amber-300 text-sm font-medium disabled:text-gray-500">Run All</button>
              </div>
              <div className="divide-y divide-gray-700">
                {category.endpoints.map((endpoint) => {
                  const key = `${categoryKey}-${endpoint.path}`;
                  const result = results[key];
                  const isExpanded = expandedResults[key];
                  return (
                    <div key={endpoint.path} className="p-4">
                      <div className="flex items-center gap-4">
                        <span className={`px-2 py-1 rounded text-xs font-bold min-w-[50px] text-center ${endpoint.method === 'GET' ? 'bg-green-900 text-green-400' : endpoint.method === 'POST' ? 'bg-blue-900 text-blue-400' : 'bg-yellow-900 text-yellow-400'}`}>{endpoint.method}</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <code className="text-white font-mono text-sm">{endpoint.path}</code>
                            {endpoint.isV2 && <span className="text-xs bg-purple-900 text-purple-300 px-1.5 py-0.5 rounded">v2</span>}
                          </div>
                          <p className="text-gray-400 text-xs mt-0.5">{endpoint.description}</p>
                        </div>
                        {result && (
                          <div className="flex items-center gap-3">
                            {result.time !== null && <span className={`text-xs ${result.time < 500 ? 'text-green-400' : result.time < 1000 ? 'text-yellow-400' : 'text-red-400'}`}>{formatTime(result.time)}</span>}
                            {result.statusCode > 0 && <span className="text-xs text-gray-500">{result.statusCode}</span>}
                            <TestStatus status={result.status} />
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <button onClick={() => runTest(categoryKey, endpoint)} disabled={isRunningAll || result?.status === 'running'} className="text-amber-400 hover:text-amber-300 text-sm disabled:text-gray-500">Test</button>
                          {result && <button onClick={() => toggleExpanded(key)} className="text-gray-400 hover:text-white text-sm">{isExpanded ? '▼' : '▶'}</button>}
                        </div>
                      </div>
                      {result && isExpanded && (
                        <div className="mt-3 bg-gray-900 rounded p-3 overflow-hidden">
                          {result.error && <div className="text-red-400 text-sm mb-2">Error: {result.error}</div>}
                          <div className="max-h-64 overflow-auto"><pre className="text-xs text-gray-300 whitespace-pre-wrap">{formatJson(result.response)}</pre></div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function UserSimulatorTab() {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [actionResult, setActionResult] = useState(null);
  const [selectedTier, setSelectedTier] = useState(4); // Default to Elite I
  const [xpAmount, setXpAmount] = useState(1000);
  const [userStatus, setUserStatus] = useState(null);
  const [userAchievements, setUserAchievements] = useState([]);
  const [allAchievements, setAllAchievements] = useState([]);
  const [rewards, setRewards] = useState([]);

  // Search users
  const searchUsers = async () => {
    if (!searchTerm.trim()) return;
    setLoading(true);
    try {
      const response = await api.get(`/admin/users?search=${encodeURIComponent(searchTerm)}&limit=20`);
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Error searching users:', error);
      setActionResult({ type: 'error', message: 'Failed to search users' });
    }
    setLoading(false);
  };

  // Load user details
  const loadUserDetails = async (user) => {
    setSelectedUser(user);
    setLoading(true);
    try {
      // Load user status
      const statusRes = await api.get(`/admin/dev/user-status/${user.id}`);
      setUserStatus(statusRes.data);
      
      // Load user achievements
      const achievementsRes = await api.get(`/admin/dev/user-achievements/${user.id}`);
      setUserAchievements(achievementsRes.data.achievements || []);
      setAllAchievements(achievementsRes.data.achievements || []);
      
      // Load available rewards
      const rewardsRes = await api.get('/admin/rewards');
      setRewards(rewardsRes.data.rewards || []);
    } catch (error) {
      console.error('Error loading user details:', error);
    }
    setLoading(false);
  };

  // Force tier change
  const forceTierChange = async () => {
    if (!selectedUser) return;
    setLoading(true);
    setActionResult(null);
    try {
      const tierInfo = TIER_LEVELS.find(t => t.level === selectedTier);
      const response = await api.post(`/admin/dev/force-tier/${selectedUser.id}`, {
        tier: tierInfo.tier,
        level: tierInfo.level,
        sendNotification: true
      });
      setActionResult({ type: 'success', message: `Tier changed to ${tierInfo.name}. Notification sent!` });
      await loadUserDetails(selectedUser);
    } catch (error) {
      setActionResult({ type: 'error', message: error.response?.data?.error || 'Failed to change tier' });
    }
    setLoading(false);
  };

  // Add XP
  const addXP = async () => {
    if (!selectedUser) return;
    setLoading(true);
    setActionResult(null);
    try {
      const response = await api.post(`/admin/dev/add-xp/${selectedUser.id}`, {
        amount: xpAmount,
        source: 'dev_tools_test',
        description: 'Added via Dev Tools for testing'
      });
      setActionResult({ type: 'success', message: `Added ${xpAmount} XP. New total: ${response.data.newXP}` });
      await loadUserDetails(selectedUser);
    } catch (error) {
      setActionResult({ type: 'error', message: error.response?.data?.error || 'Failed to add XP' });
    }
    setLoading(false);
  };

  // Remove test XP
  const removeTestXP = async (resetAchievements = false) => {
    if (!selectedUser) return;
    const confirmMsg = resetAchievements 
      ? 'Remove all DevTools-added test XP AND reset all achievements for this user?\n\nThis will delete all achievements and re-award only those the user legitimately qualifies for.'
      : 'Remove all DevTools-added test XP for this user?';
    if (!window.confirm(confirmMsg)) return;
    setLoading(true);
    setActionResult(null);
    try {
      const url = resetAchievements 
        ? `/admin/dev/remove-test-xp/${selectedUser.id}?resetAchievements=true`
        : `/admin/dev/remove-test-xp/${selectedUser.id}`;
      const response = await api.delete(url);
      setActionResult({ type: 'success', message: response.data.message });
      await loadUserDetails(selectedUser);
    } catch (error) {
      setActionResult({ type: 'error', message: error.response?.data?.error || 'Failed to remove test XP' });
    }
    setLoading(false);
  };

  // Trigger level up notification
  const triggerLevelUp = async () => {
    if (!selectedUser) return;
    setLoading(true);
    setActionResult(null);
    try {
      await api.post(`/admin/dev/test-level-up/${selectedUser.id}`);
      setActionResult({ type: 'success', message: 'Level-up notification triggered!' });
    } catch (error) {
      setActionResult({ type: 'error', message: error.response?.data?.error || 'Failed to trigger level up' });
    }
    setLoading(false);
  };

  // Trigger tier upgrade notification
  const triggerTierUpgrade = async () => {
    if (!selectedUser) return;
    setLoading(true);
    setActionResult(null);
    try {
      await api.post(`/admin/dev/test-tier-upgrade/${selectedUser.id}`);
      setActionResult({ type: 'success', message: 'Tier upgrade notification triggered!' });
    } catch (error) {
      setActionResult({ type: 'error', message: error.response?.data?.error || 'Failed to trigger tier upgrade' });
    }
    setLoading(false);
  };

  // Unlock achievement
  const unlockAchievement = async (achievementId) => {
    if (!selectedUser) return;
    setLoading(true);
    setActionResult(null);
    try {
      await api.post(`/admin/dev/unlock-achievement/${selectedUser.id}/${achievementId}`, {
        sendNotification: true
      });
      setActionResult({ type: 'success', message: 'Achievement unlocked! Notification sent.' });
      await loadUserDetails(selectedUser);
    } catch (error) {
      setActionResult({ type: 'error', message: error.response?.data?.error || 'Failed to unlock achievement' });
    }
    setLoading(false);
  };

  // Reset achievement progress
  const resetAchievementProgress = async (achievementId) => {
    if (!selectedUser) return;
    setLoading(true);
    setActionResult(null);
    try {
      await api.post(`/admin/dev/reset-achievement/${selectedUser.id}/${achievementId}`);
      setActionResult({ type: 'success', message: 'Achievement progress reset.' });
      await loadUserDetails(selectedUser);
    } catch (error) {
      setActionResult({ type: 'error', message: error.response?.data?.error || 'Failed to reset achievement' });
    }
    setLoading(false);
  };

  // Assign reward
  const assignReward = async (rewardId) => {
    if (!selectedUser) return;
    setLoading(true);
    setActionResult(null);
    try {
      await api.post(`/admin/dev/assign-reward/${rewardId}`, {
        userId: selectedUser.id
      });
      setActionResult({ type: 'success', message: 'Reward assigned to user!' });
    } catch (error) {
      setActionResult({ type: 'error', message: error.response?.data?.error || 'Failed to assign reward' });
    }
    setLoading(false);
  };

  // Recalculate user status
  const recalculateStatus = async () => {
    if (!selectedUser) return;
    setLoading(true);
    setActionResult(null);
    try {
      const response = await api.post(`/admin/dev/recalculate-status/${selectedUser.id}`);
      setActionResult({ type: 'success', message: `Status recalculated. New status: ${response.data.displayName}` });
      await loadUserDetails(selectedUser);
    } catch (error) {
      setActionResult({ type: 'error', message: error.response?.data?.error || 'Failed to recalculate status' });
    }
    setLoading(false);
  };

  // Recalculate user achievements and sync XP
  const recalculateAchievements = async () => {
    if (!selectedUser) return;
    setLoading(true);
    setActionResult(null);
    try {
      const response = await api.post(`/admin/dev/recalculate-achievements/${selectedUser.id}`);
      const { newUnlocks, xpSynced, totalAchievements, totalAchievementXP } = response.data;
      let message = `Achievements recalculated. ${newUnlocks || 0} new unlocks.`;
      if (xpSynced > 0) {
        message += ` Synced ${xpSynced} missing XP.`;
      }
      message += ` Total: ${totalAchievements} achievements, ${totalAchievementXP} XP.`;
      setActionResult({ type: 'success', message });
      await loadUserDetails(selectedUser);
    } catch (error) {
      setActionResult({ type: 'error', message: error.response?.data?.error || 'Failed to recalculate achievements' });
    }
    setLoading(false);
  };

  return (
    <div className="space-y-6">
      {/* User Search */}
      <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
        <h3 className="text-lg font-semibold text-white mb-4">🔍 Find User</h3>
        <div className="flex gap-3">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && searchUsers()}
            placeholder="Search by email, name, or member number..."
            className="flex-1 bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white placeholder-gray-400 focus:ring-2 focus:ring-amber-500"
          />
          <button onClick={searchUsers} disabled={loading} className="bg-amber-600 hover:bg-amber-700 text-white px-6 py-2 rounded font-medium">Search</button>
        </div>
        
        {users.length > 0 && (
          <div className="mt-4 max-h-48 overflow-auto">
            <table className="w-full text-sm">
              <thead className="text-gray-400 border-b border-gray-700">
                <tr><th className="text-left py-2">Email</th><th className="text-left py-2">Name</th><th className="text-left py-2">Member #</th><th className="text-left py-2">Role</th><th></th></tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {users.map(user => (
                  <tr key={user.id} className={`hover:bg-gray-700 ${selectedUser?.id === user.id ? 'bg-amber-900/30' : ''}`}>
                    <td className="py-2 text-white">{user.email}</td>
                    <td className="py-2 text-gray-300">{user.firstName} {user.lastName}</td>
                    <td className="py-2 text-gray-400 font-mono">{user.playerClubMember?.memberNumber || '-'}</td>
                    <td className="py-2"><span className={`text-xs px-2 py-0.5 rounded ${user.role === 'admin' ? 'bg-purple-900 text-purple-300' : user.role === 'staff' ? 'bg-blue-900 text-blue-300' : 'bg-gray-700 text-gray-300'}`}>{user.role}</span></td>
                    <td className="py-2"><button onClick={() => loadUserDetails(user)} className="text-amber-400 hover:text-amber-300 text-sm">Select</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Action Result */}
      {actionResult && (
        <div className={`p-4 rounded-lg ${actionResult.type === 'success' ? 'bg-green-900/50 border border-green-700 text-green-300' : 'bg-red-900/50 border border-red-700 text-red-300'}`}>
          {actionResult.type === 'success' ? '✅' : '❌'} {actionResult.message}
        </div>
      )}

      {selectedUser && (
        <>
          {/* User Info */}
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-white">{selectedUser.firstName} {selectedUser.lastName}</h3>
                <p className="text-gray-400 text-sm">{selectedUser.email}</p>
              </div>
              {userStatus && (
                <div className="text-right">
                  <div className="text-2xl font-bold text-amber-400">{userStatus.effectiveStatus?.displayName || userStatus.storedStatus?.displayName || 'VIP I'}</div>
                  <div className="text-sm text-gray-400">{(userStatus.currentPeriod?.xp || 0).toLocaleString()} XP this period</div>
                </div>
              )}
            </div>
            {userStatus && (
              <div className="grid grid-cols-4 gap-4 text-center">
                <div className="bg-gray-700 rounded p-3">
                  <div className="text-xl font-bold text-white">{userStatus.effectiveStatus?.level || userStatus.storedStatus?.level || 1}</div>
                  <div className="text-xs text-gray-400">Level</div>
                </div>
                <div className="bg-gray-700 rounded p-3">
                  <div className="text-xl font-bold text-white">{(userStatus.currentPeriod?.xp || 0).toLocaleString()}</div>
                  <div className="text-xs text-gray-400">Period XP</div>
                </div>
                <div className="bg-gray-700 rounded p-3">
                  <div className="text-xl font-bold text-white">{(userStatus.lifetime?.xp || 0).toLocaleString()}</div>
                  <div className="text-xs text-gray-400">Lifetime XP</div>
                </div>
                <div className="bg-gray-700 rounded p-3">
                  <div className="text-xl font-bold text-white">{userAchievements.filter(a => a.unlockedAt).length}</div>
                  <div className="text-xs text-gray-400">Achievements</div>
                </div>
              </div>
            )}
          </div>

          {/* Tier Controls */}
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <h3 className="text-lg font-semibold text-white mb-4">⭐ Status & Tier Controls</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Force Tier Change</label>
                <div className="flex gap-2">
                  <select value={selectedTier} onChange={(e) => setSelectedTier(Number(e.target.value))} className="flex-1 bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white">
                    {TIER_LEVELS.map(t => (
                      <option key={t.level} value={t.level}>{t.name} (Level {t.level})</option>
                    ))}
                  </select>
                  <button onClick={forceTierChange} disabled={loading} className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded font-medium">Set Tier</button>
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">Add XP</label>
                <div className="flex gap-2">
                  <input type="number" value={xpAmount} onChange={(e) => setXpAmount(Number(e.target.value))} className="flex-1 bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white" />
                  <button onClick={addXP} disabled={loading} className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded font-medium">Add XP</button>
                  <button onClick={() => removeTestXP(false)} disabled={loading} className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded font-medium" title="Remove all DevTools-added test XP">🗑️</button>
                  <button onClick={() => removeTestXP(true)} disabled={loading} className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded font-medium text-xs" title="Remove test XP AND reset achievements">🗑️🏆</button>
                </div>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <button onClick={triggerLevelUp} disabled={loading} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm">🔔 Test Level-Up Notification</button>
              <button onClick={triggerTierUpgrade} disabled={loading} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm">🔔 Test Tier Upgrade Notification</button>
              <button onClick={recalculateStatus} disabled={loading} className="bg-gray-600 hover:bg-gray-500 text-white px-4 py-2 rounded text-sm">🔄 Recalculate Status</button>
            </div>
          </div>

          {/* Achievements Controls */}
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">🏆 Achievements</h3>
              <button onClick={recalculateAchievements} disabled={loading} className="text-amber-400 hover:text-amber-300 text-sm">🔄 Recalculate All</button>
            </div>
            <div className="max-h-64 overflow-auto">
              <table className="w-full text-sm">
                <thead className="text-gray-400 border-b border-gray-700 sticky top-0 bg-gray-800">
                  <tr><th className="text-left py-2">Achievement</th><th className="text-left py-2">Progress</th><th className="text-left py-2">Status</th><th></th></tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {allAchievements.map(achievement => {
                    const isUnlocked = achievement.isUnlocked || !!achievement.unlockedAt;
                    return (
                      <tr key={achievement.id} className="hover:bg-gray-700">
                        <td className="py-2">
                          <div className="text-white">{achievement.name}</div>
                          <div className="text-xs text-gray-500">{achievement.xp || achievement.xpReward || 0} XP</div>
                        </td>
                        <td className="py-2 text-gray-300">{achievement.requirementValue || '?'} {achievement.requirementLabel || achievement.requirementType || ''}</td>
                        <td className="py-2">
                          {isUnlocked ? (
                            <span className="text-green-400">✅ Unlocked</span>
                          ) : (
                            <span className="text-gray-500">🔒 Locked</span>
                          )}
                        </td>
                        <td className="py-2">
                          <div className="flex gap-2">
                            {!isUnlocked && (
                              <button onClick={() => unlockAchievement(achievement.id)} className="text-green-400 hover:text-green-300 text-xs">Unlock</button>
                            )}
                            {isUnlocked && (
                              <button onClick={() => resetAchievementProgress(achievement.id)} className="text-red-400 hover:text-red-300 text-xs">Reset</button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Rewards Controls */}
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <h3 className="text-lg font-semibold text-white mb-4">🎁 Assign Rewards</h3>
            {rewards.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                {rewards.filter(r => r.isActive).map(reward => (
                  <div key={reward.id} className="bg-gray-700 rounded p-3 flex items-center justify-between">
                    <div>
                      <div className="text-white text-sm font-medium">{reward.title}</div>
                      <div className="text-xs text-gray-400">{reward.requiresStaffAuth ? '🔐 Staff Auth' : '🎫 Self-Redeem'}</div>
                    </div>
                    <button onClick={() => assignReward(reward.id)} className="bg-amber-600 hover:bg-amber-700 text-white px-3 py-1 rounded text-sm">Assign</button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-400">No active rewards found. Create rewards in the Rewards Manager.</p>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function NotificationTesterTab() {
  const [title, setTitle] = useState('Test Notification');
  const [body, setBody] = useState('This is a test push notification from Dev Tools');
  const [targetType, setTargetType] = useState('self');
  const [targetUserId, setTargetUserId] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const sendNotification = async () => {
    setLoading(true);
    setResult(null);
    try {
      let payload = { title, body };

      if (targetType === 'self') {
        // Send to self (current admin)
        await api.post('/admin/dev/send-push', payload);
      } else if (targetType === 'user' && targetUserId) {
        payload.userId = targetUserId;
        await api.post('/admin/dev/send-push', payload);
      } else if (targetType === 'all') {
        payload.sendToAll = true;
        await api.post('/admin/dev/send-push', payload);
      } else {
        setResult({ type: 'error', message: 'Please specify a target user ID' });
        setLoading(false);
        return;
      }

      setResult({ type: 'success', message: 'Notification sent successfully!' });
    } catch (error) {
      setResult({ type: 'error', message: error.response?.data?.error || 'Failed to send notification' });
    }
    setLoading(false);
  };

  const sendTestNotificationTypes = async (type) => {
    setLoading(true);
    setResult(null);
    try {
      await api.post(`/admin/dev/test-notification`, { type });
      setResult({ type: 'success', message: `${type} notification sent!` });
    } catch (error) {
      setResult({ type: 'error', message: error.response?.data?.error || 'Failed to send notification' });
    }
    setLoading(false);
  };

  return (
    <div className="space-y-6">
      {/* Custom Notification */}
      <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
        <h3 className="text-lg font-semibold text-white mb-4">📤 Send Custom Notification</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-gray-400 mb-2">Title</label>
            <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white" />
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-2">Body</label>
            <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3} className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white" />
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-2">Target</label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 text-white">
                <input type="radio" value="self" checked={targetType === 'self'} onChange={(e) => setTargetType(e.target.value)} className="text-amber-500" /> My Device
              </label>
              <label className="flex items-center gap-2 text-white">
                <input type="radio" value="user" checked={targetType === 'user'} onChange={(e) => setTargetType(e.target.value)} className="text-amber-500" /> Specific User
              </label>
              <label className="flex items-center gap-2 text-white">
                <input type="radio" value="all" checked={targetType === 'all'} onChange={(e) => setTargetType(e.target.value)} className="text-amber-500" /> All Users
              </label>
            </div>
            {targetType === 'user' && (
              <input type="text" value={targetUserId} onChange={(e) => setTargetUserId(e.target.value)} placeholder="User ID" className="mt-2 w-full bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white" />
            )}
          </div>
          <button onClick={sendNotification} disabled={loading} className="bg-amber-600 hover:bg-amber-700 text-white px-6 py-2 rounded font-medium">
            {loading ? 'Sending...' : 'Send Notification'}
          </button>
        </div>
      </div>

      {/* Quick Test Notifications */}
      <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
        <h3 className="text-lg font-semibold text-white mb-4">⚡ Quick Test Notifications</h3>
        <p className="text-gray-400 text-sm mb-4">Send pre-configured test notifications to your device</p>
        <div className="flex flex-wrap gap-3">
          <button onClick={() => sendTestNotificationTypes('tier-upgrade')} disabled={loading} className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded text-sm">🎉 Tier Upgrade</button>
          <button onClick={() => sendTestNotificationTypes('level-up')} disabled={loading} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm">⬆️ Level Up</button>
          <button onClick={() => sendTestNotificationTypes('achievement')} disabled={loading} className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded text-sm">🏆 Achievement</button>
          <button onClick={() => sendTestNotificationTypes('reward')} disabled={loading} className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded text-sm">🎁 New Reward</button>
          <button onClick={() => sendTestNotificationTypes('waitlist')} disabled={loading} className="bg-cyan-600 hover:bg-cyan-700 text-white px-4 py-2 rounded text-sm">📋 Waitlist Update</button>
        </div>
      </div>

      {/* Result */}
      {result && (
        <div className={`p-4 rounded-lg ${result.type === 'success' ? 'bg-green-900/50 border border-green-700 text-green-300' : 'bg-red-900/50 border border-red-700 text-red-300'}`}>
          {result.type === 'success' ? '✅' : '❌'} {result.message}
        </div>
      )}
    </div>
  );
}

function SystemHealthTab() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(false);
  const apiBaseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';
  // Get base URL without /api/v1 for health endpoint
  const serverBaseUrl = apiBaseUrl.replace('/api/v1', '');

  const checkHealth = async () => {
    setLoading(true);
    const results = {};

    // Check various services - note different base URLs
    const checks = [
      { name: 'API Server', endpoint: '/health', key: 'api', useServerBase: true },
      { name: 'Database', endpoint: '/admin/dev/health/database', key: 'database', useServerBase: false },
      { name: 'TableCaptain Bridge', endpoint: '/tablecaptain/health', key: 'tablecaptain', useServerBase: false },
      { name: 'Firebase', endpoint: '/admin/dev/health/firebase', key: 'firebase', useServerBase: false },
      { name: 'PokerAtlas', endpoint: '/pokeratlas/status', key: 'pokeratlas', useServerBase: false },
    ];

    for (const check of checks) {
      try {
        const start = Date.now();
        if (check.useServerBase) {
          // Direct fetch without auth for /health
          const response = await fetch(`${serverBaseUrl}${check.endpoint}`);
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          results[check.key] = { status: 'healthy', latency: Date.now() - start };
        } else {
          // Use api instance with auth
          await api.get(check.endpoint);
          results[check.key] = { status: 'healthy', latency: Date.now() - start };
        }
      } catch (error) {
        results[check.key] = { status: 'unhealthy', error: error.message };
      }
    }

    setHealth(results);
    setLoading(false);
  };

  useEffect(() => {
    checkHealth();
  }, []);

  return (
    <div className="space-y-6">
      <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">🏥 System Health</h3>
          <button onClick={checkHealth} disabled={loading} className="text-amber-400 hover:text-amber-300 text-sm">🔄 Refresh</button>
        </div>
        
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500 mx-auto"></div>
            <p className="text-gray-400 mt-2">Checking services...</p>
          </div>
        ) : health ? (
          <div className="space-y-3">
            {[
              { key: 'api', name: 'API Server', icon: '🖥️' },
              { key: 'database', name: 'Database', icon: '🗄️' },
              { key: 'tablecaptain', name: 'TableCaptain Bridge', icon: '🌉' },
              { key: 'firebase', name: 'Firebase', icon: '🔥' },
              { key: 'pokeratlas', name: 'PokerAtlas', icon: '🃏' },
            ].map(service => {
              const status = health[service.key];
              return (
                <div key={service.key} className="flex items-center justify-between bg-gray-700 rounded p-3">
                  <div className="flex items-center gap-3">
                    <span className="text-xl">{service.icon}</span>
                    <span className="text-white">{service.name}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    {status?.latency && (
                      <span className={`text-xs ${status.latency < 200 ? 'text-green-400' : status.latency < 500 ? 'text-yellow-400' : 'text-red-400'}`}>
                        {status.latency}ms
                      </span>
                    )}
                    <span className={`px-2 py-1 rounded text-xs font-medium ${status?.status === 'healthy' ? 'bg-green-900 text-green-400' : 'bg-red-900 text-red-400'}`}>
                      {status?.status === 'healthy' ? '✓ Healthy' : '✗ Unhealthy'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : null}
      </div>

      {/* Environment Info */}
      <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
        <h3 className="text-lg font-semibold text-white mb-4">📋 Environment Info</h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-400">API URL:</span>
            <code className="text-amber-400">{import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1'}</code>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Environment:</span>
            <code className="text-amber-400">{import.meta.env.MODE}</code>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Build Time:</span>
            <code className="text-gray-500">{new Date().toISOString()}</code>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function DevTools() {
  const [activeTab, setActiveTab] = useState('api');
  const [results, setResults] = useState({});
  const [expandedResults, setExpandedResults] = useState({});
  const [isRunningAll, setIsRunningAll] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const apiBaseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';

  const tabs = [
    { id: 'api', label: 'API Testing', icon: '🔌' },
    { id: 'user', label: 'User Simulator', icon: '👤' },
    { id: 'notifications', label: 'Notifications', icon: '🔔' },
    { id: 'health', label: 'System Health', icon: '🏥' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white">Developer Tools</h1>
          <p className="text-gray-400 mt-1">API Testing, User Simulation & System Diagnostics</p>
        </div>
      </div>

      {/* API Info */}
      <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium text-gray-400">API Base URL</h3>
            <p className="text-white font-mono text-sm mt-1">{apiBaseUrl}</p>
          </div>
          <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-700">
        <div className="flex gap-1">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 text-sm font-medium rounded-t-lg transition-colors ${
                activeTab === tab.id
                  ? 'bg-gray-800 text-amber-400 border-b-2 border-amber-400'
                  : 'text-gray-400 hover:text-white hover:bg-gray-800/50'
              }`}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'api' && (
        <ApiTestingTab
          results={results}
          setResults={setResults}
          expandedResults={expandedResults}
          setExpandedResults={setExpandedResults}
          isRunningAll={isRunningAll}
          setIsRunningAll={setIsRunningAll}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
          apiBaseUrl={apiBaseUrl}
        />
      )}
      {activeTab === 'user' && <UserSimulatorTab />}
      {activeTab === 'notifications' && <NotificationTesterTab />}
      {activeTab === 'health' && <SystemHealthTab />}
    </div>
  );
}
